import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  truncateText,
  generateId,
  searchAndSortConversations,
} from '@/lib/utils';
import { FileNode, buildFileTree } from '@/lib/fileUtils';
import { apiPost, apiPatch, apiDelete } from '@/lib/api';
import { useConversations } from '@/hooks/conversation/useConversations';
import { useBoxSelection } from '@/hooks/ui/useBoxSelection';
import { useConversationActions } from '@/hooks/conversation/useConversationActions';
import { useFileUploadWithProgress } from '@/hooks/file/useFileUploadWithProgress';
import { useFolderOperations } from '@/hooks/sidebar/useFolderOperations';
import { useConfirmDialog } from '@/hooks/ui/useConfirmDialog';
import { showToast } from '@/components/Toast';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import { useFilesStore, useUIStore } from '@/stores';
import { createPendingId } from '@/utils/messageUtils';

export interface UseProjectDetailProps {
  projectId: string;
  projectName: string;
  projectInstructions?: string;
  projectStarred?: boolean;
  projectArchived?: boolean;
  onFileUpload: (files: File[], targetPath?: string) => void;
  onConversationSelect?: (conversationId: string, projectId: string) => void;
  onSwitchToChat?: (
    conversationId: string,
    projectId: string,
    messageDetails?: {
      content: string;
      isRetry?: boolean;
      options?: {
        usePublic?: boolean;
        allProjects?: boolean;
        thinkType?: 'think' | 'no-think' | 'hard-think';
        clientId?: string | null;
        pendingId?: string; // 🔥 架构简化：移到 options 中，可选
      };
    }
  ) => void;
  onStarProject?: () => Promise<void>;
  onArchiveProject?: (projectId: string, archived: boolean) => Promise<void>;
  onUpdateProject?: (project: {
    id: string;
    name: string;
    description: string;
    instructions: string;
  }) => void;
}

export function useProjectDetail({
  projectId,
  projectInstructions = '',
  projectStarred = false,
  projectArchived = false,
  onFileUpload,
  onConversationSelect: externalOnConversationSelect,
  onSwitchToChat,
  onStarProject,
  onArchiveProject,
  onUpdateProject,
}: UseProjectDetailProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery] = useState('');

  // Instructions state
  const [instructions, setInstructions] = useState(projectInstructions);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);

  // Members management state
  const [showMembers, setShowMembers] = useState(false);
  const [tempInstructions, setTempInstructions] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);

  // Starred state
  const [isStarred, setIsStarred] = useState(projectStarred);
  const [isStarring, setIsStarring] = useState(false);

  // Archived state
  const [isArchived, setIsArchived] = useState(projectArchived);
  const [isArchiving, setIsArchiving] = useState(false);

  // Edit project modal state
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);

  // Hover state for folder actions
  const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null);

  // Conversation selection state
  const [selectedConversationIds, setSelectedConversationIds] = useState<
    Set<string>
  >(new Set());

  // Conversation confirm dialog (for bulk delete and single delete)
  const {
    confirmWithAction: confirmConversationAction,
    dialogProps: conversationDialogProps,
  } = useConfirmDialog();

  // Conversation rename state
  const [renameConversationId, setRenameConversationId] = useState<
    string | null
  >(null);
  const [renameValue, setRenameValue] = useState('');
  const [showConversationRenameModal, setShowConversationRenameModal] =
    useState(false);
  const [isRenamingConversation, setIsRenamingConversation] = useState(false);

  // Share modal state
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    conversationId: string;
    conversationTitle: string;
  }>({
    isOpen: false,
    conversationId: '',
    conversationTitle: '',
  });

  // File selection and hover state
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set()
  );
  const [hoveredFileId, setHoveredFileId] = useState<string | null>(null);

  // Files box selection refs
  const filesContainerRef = useRef<HTMLDivElement>(null);
  const fileItemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Files browser state
  const [fileTree, setFileTree] = useState<FileNode[]>([]);
  const [currentPath, setCurrentPath] = useState<FileNode[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [fileSearchQuery, setFileSearchQuery] = useState('');

  // File preview state
  const [previewFile, setPreviewFile] = useState<FileNode | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);

  // Add text content modal state
  const [isAddTextContentModalOpen, setIsAddTextContentModalOpen] =
    useState(false);
  const [addTextContentTitle, setAddTextContentTitle] = useState('');
  const [addTextContentContent, setAddTextContentContent] = useState('');
  const [isAddingTextContent, setIsAddingTextContent] = useState(false);

  // Get projectExpand and setSidebarProjectConversationsCache from global context
  const { projectExpand, setSidebarProjectConversationsCache } =
    useConversationsContext();

  // Use the conversations hook to manage project conversations
  const {
    conversations,
    isLoading,
    loadConversations,
    setConversations,
    setCurrentConversationId,
  } = useConversations(projectId);

  // 过滤出只属于当前项目的会话
  const projectConversations = useMemo(
    () => conversations.filter(conv => conv.projectId === projectId),
    [conversations, projectId]
  );

  // Apply search and sort
  const filteredConversations = useMemo(
    () => searchAndSortConversations(projectConversations, searchQuery),
    [projectConversations, searchQuery]
  );

  // Check if any conversation is currently loading
  const hasLoadingConversation = filteredConversations.some(
    conv => conv.isLoading
  );

  // ProjectDetail pagination: default 10, then +20 per click
  const [visibleConversationCount, setVisibleConversationCount] = useState(10);

  useEffect(() => {
    setVisibleConversationCount(10);
  }, [projectId, searchQuery]);

  const visibleConversations = useMemo(
    () => filteredConversations.slice(0, visibleConversationCount),
    [filteredConversations, visibleConversationCount]
  );

  const hasMoreConversations =
    filteredConversations.length > visibleConversationCount;

  const handleShowMoreConversations = useCallback(() => {
    setVisibleConversationCount(prev =>
      Math.min(filteredConversations.length, prev + 20)
    );
  }, [filteredConversations.length]);

  // 使用 filesStore 加载项目文件
  const loadProjectFilesFromStore = useFilesStore(
    state => state.loadProjectFiles
  );
  const resetProjectFilesCache = useFilesStore(
    state => state.resetProjectFilesCache
  );
  const fileTreeFromStore = useFilesStore(
    state => state.projectFilesCache[projectId]?.fileTree
  );
  const isLoadingFromStore = useFilesStore(
    state => state.projectFilesCache[projectId]?.isLoading
  );

  // 同步 fileTree 和 loading 状态
  useEffect(() => {
    if (fileTreeFromStore) {
      setFileTree(buildFileTree(fileTreeFromStore));
    }
  }, [fileTreeFromStore]);

  useEffect(() => {
    setIsLoadingFiles(isLoadingFromStore || false);
  }, [isLoadingFromStore]);

  // 同步 projectInstructions prop 到 instructions state
  useEffect(() => {
    setInstructions(projectInstructions);
  }, [projectInstructions]);

  // 同步 projectStarred prop 到 isStarred state
  useEffect(() => {
    setIsStarred(projectStarred);
  }, [projectStarred]);

  // 同步 projectArchived prop 到 isArchived state
  useEffect(() => {
    setIsArchived(projectArchived);
  }, [projectArchived]);

  // Handle toggle star
  const handleToggleStar = async () => {
    if (isStarring) return;

    setIsStarring(true);
    try {
      const data = await apiPost<any>(`/api/projects/${projectId}/star`, {
        starred: !isStarred,
      });

      if (!data || data.code !== 200) {
        return;
      }

      setIsStarred(!isStarred);
      // 刷新侧边栏
      await onStarProject?.();
      showToast(
        isStarred ? t('toast.removedFromStarred') : t('toast.addedToStarred'),
        'success'
      );
    } catch (error) {
      console.error('Failed to update star status:', error);
      showToast(t('toast.failedToUpdateStarStatus'), 'error');
    } finally {
      setIsStarring(false);
    }
  };

  const handleToggleArchive = async () => {
    if (isArchiving) return;

    setIsArchiving(true);
    try {
      // Archive 时传 archived: true，Unarchive 时传 archived: false
      const archivedValue = !isArchived;
      await onArchiveProject?.(projectId, archivedValue);

      setIsArchived(archivedValue);
      showToast(
        archivedValue
          ? t('toast.projectArchived')
          : t('toast.projectUnarchived'),
        'success'
      );
    } catch (error) {
      console.error('Failed to update archive status:', error);
      showToast(t('toast.failedToUpdateArchiveStatus'), 'error');
    } finally {
      setIsArchiving(false);
    }
  };

  // 获取当前路径下的文件/文件夹
  const currentNodes = useMemo(() => {
    let nodes: FileNode[];
    if (currentPath.length === 0) {
      nodes = fileTree;
    } else {
      let current = fileTree;
      for (const pathPart of currentPath) {
        // 支持通过 name 或 id 查找文件夹
        const folder = current.find(
          n => n.type === 'folder' && n.id === pathPart.id
        );
        if (folder && folder.children) {
          current = folder.children;
        } else {
          return [];
        }
      }
      nodes = current;
    }

    // 如果有搜索查询，过滤节点
    if (fileSearchQuery.trim()) {
      const query = fileSearchQuery?.trim()?.toLowerCase();
      const filterNodes = (nodeList: FileNode[]): FileNode[] => {
        return nodeList
          .filter(node => {
            const nameMatch = node.name.toLowerCase().includes(query);
            if (node.type === 'folder' && node.children) {
              const childrenMatch = filterNodes(node.children).length > 0;
              return nameMatch || childrenMatch;
            }
            return nameMatch;
          })
          .map(node => {
            if (node.type === 'folder' && node.children) {
              return {
                ...node,
                children: filterNodes(node.children),
              };
            }
            return node;
          });
      };
      nodes = filterNodes(nodes);
    }

    return nodes;
  }, [fileTree, currentPath, fileSearchQuery]);

  // 获取当前所在文件夹的 ID
  const getCurrentFolderId = useCallback(() => {
    if (currentPath.length === 0) {
      return null;
    }
    // 返回当前路径最后一个文件夹的 ID
    return currentPath[currentPath.length - 1]?.path || null;
  }, [currentPath]);

  // 使用 useFileUploadWithProgress hook
  const fileUploadHook = useFileUploadWithProgress({
    projectId,
    getCurrentFolderId,
    resetProjectFilesCache,
    loadProjectFilesFromStore,
  });

  // 清空文件选中
  const clearFileSelection = useCallback(() => {
    setSelectedFileIds(new Set());
  }, []);

  // 使用 useFolderOperations hook
  const folderOperationsHook = useFolderOperations({
    projectId,
    fileTree,
    selectedFileIds,
    getCurrentFolderId,
    resetProjectFilesCache,
    loadProjectFilesFromStore,
    clearFileSelection,
    // 预览联动：删除文件时关闭预览，重命名文件时更新预览
    previewFilePath: previewFile?.path || null,
    onClosePreview: () => {
      setPreviewFile(null);
      setPreviewFileUrl(null);
    },
    onUpdatePreview: (newFilename, newFilePath, newFileUrl) => {
      setPreviewFile(prev =>
        prev ? { ...prev, name: newFilename, path: newFilePath } : null
      );
      setPreviewFileUrl(newFileUrl);
    },
  });

  // 切换文件选中状态
  const toggleFileSelect = (fileId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedFileIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Handle batch select from box selection (files)
  const handleFileBatchSelect = useCallback((ids: string[]) => {
    setSelectedFileIds(prev => {
      const newSet = new Set(prev);
      ids.forEach(id => newSet.add(id));
      return newSet;
    });
  }, []);

  // Use box selection hook for files
  const filesBoxSelection = useBoxSelection({
    containerRef: filesContainerRef,
    itemRefs: fileItemRefs,
    onSelect: handleFileBatchSelect,
  });

  // Get all file IDs from current folder only (not recursive)
  const getCurrentFolderFileIds = useCallback((nodes: FileNode[]): string[] => {
    const ids: string[] = [];
    for (const node of nodes) {
      if (node.id) {
        ids.push(node.id);
      }
    }
    return ids;
  }, []);

  // Handle Select All files (current folder only)
  const handleSelectAllFiles = useCallback(() => {
    const currentFolderIds = getCurrentFolderFileIds(currentNodes);
    setSelectedFileIds(new Set(currentFolderIds));
  }, [currentNodes, getCurrentFolderFileIds]);

  // Check if all files in current folder are selected
  const allFilesSelected = useMemo(() => {
    const currentFolderIds = getCurrentFolderFileIds(currentNodes);
    return (
      currentFolderIds.length > 0 &&
      currentFolderIds.every(id => selectedFileIds.has(id))
    );
  }, [currentNodes, getCurrentFolderFileIds, selectedFileIds]);

  // Get total selectable file count in current folder
  const totalSelectableFiles = useMemo(() => {
    return getCurrentFolderFileIds(currentNodes).length;
  }, [currentNodes, getCurrentFolderFileIds]);

  // Calculate current folder depth (0 = root, max = 3)
  const currentFolderDepth = currentPath.length;
  const isMaxDepth = currentFolderDepth >= 3;

  // Save instructions to backend
  const handleSaveInstructions = async () => {
    setIsSavingInstructions(true);
    try {
      const trimmedInstructions = tempInstructions.trim();
      const data = await apiPatch<any>(`/api/projects/${projectId}`, {
        instructions: trimmedInstructions,
      });

      if (!data || data.code !== 200) {
        return;
      }

      setInstructions(trimmedInstructions);
      setOriginalInstructions(trimmedInstructions); // Update original after successful save
      setTempInstructions(trimmedInstructions); // Update temp to trimmed version
      setIsInstructionsModalOpen(false);
      showToast(t('toast.instructionsSavedSuccessfully'), 'success');
    } catch (error) {
      console.error('Failed to save instructions:', error);
      showToast(t('toast.failedToSaveInstructions'), 'error');
    } finally {
      setIsSavingInstructions(false);
    }
  };

  // Handle new chat
  const handleNewChat = async (
    content: string,
    isRetry?: boolean,
    options?: any
  ) => {
    if (!content.trim()) return;

    const conversationId = 'temp-' + generateId();
    // 🔥 重构：使用 createPendingId() 生成临时消息ID
    const pendingId = createPendingId();

    const userMessage = {
      id: generateId(),
      choices: [
        {
          version: 0,
          blocks: [{ type: 'text' as const, content: String(content) }],
        },
      ],
      role: 'user' as const,
      timestamp: new Date(),
    };

    // Get agent info if clientId is present
    let clientInfo = undefined;
    if (options?.clientId) {
      const agents = useUIStore.getState().agents;
      const agent = agents.find(a => a.id === options.clientId);
      if (agent) {
        clientInfo = {
          id: agent.id,
          name: agent.name,
          logo: agent.logo,
        };
      }
    }

    const assistantMessage = {
      id: pendingId, // 🔥 重构：使用 pendingId
      choices: [{ version: 0, blocks: [] }],
      role: 'assistant' as const,
      timestamp: new Date(),
      isStreaming: true,
      client: clientInfo,
    };

    // 🔥 Skill 任务：不创建 assistant 占位符消息，WebSocket handler 会完全接管
    // 否则会产生重复消息（useProjectDetail 创建一个 + WS handler 创建一个）
    const initialMessages = options?.isSkillTask
      ? [userMessage]
      : [userMessage, assistantMessage];

    const newConversation = {
      id: conversationId,
      conversationId,
      title: truncateText(content, 50),
      messages: initialMessages,
      treeData: [],
      versionPath: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      projectId,
      isLoading: true,
    };

    // 🔥 修复：添加去重逻辑，避免重复添加相同 ID 的 conversation
    setConversations(prev => [
      newConversation,
      ...prev.filter(c => c.id !== conversationId),
    ]);
    setCurrentConversationId(conversationId);

    setSidebarProjectConversationsCache(prev => ({
      ...prev,
      [projectId]: [
        newConversation,
        ...(prev[projectId] || []).filter(c => c.id !== conversationId),
      ],
    }));

    if (onSwitchToChat) {
      // 🔥 架构简化：将 pendingId 移到 options 中，而非作为单独字段
      onSwitchToChat(conversationId, projectId, {
        content,
        isRetry,
        options: {
          ...options,
          pendingId, // 传递给 sendMessage 用于匹配预创建的 assistant 消息
        },
      });
    }
  };

  // Refresh conversations for ProjectDetail
  const refreshConversations = useCallback(async () => {
    if (loadConversations) {
      // Pass force: true to ensure conversations are reloaded even if already loaded
      await loadConversations(undefined, true);
    }
  }, [loadConversations]);

  // Use conversation actions hook for ProjectDetail
  const conversationActionsHook = useConversationActions({
    onRefresh: refreshConversations,
    // Only clear the current project's cache, not the entire cache
    onCacheClear: () =>
      setSidebarProjectConversationsCache(prev => {
        const newCache = { ...prev };
        delete newCache[projectId];
        return newCache;
      }),
  });

  // Toggle conversation selection
  const toggleConversationSelect = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedConversationIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    },
    []
  );

  // Handle batch select from box selection
  const handleBatchSelect = useCallback((ids: string[]) => {
    setSelectedConversationIds(prev => {
      const newSet = new Set(prev);
      ids.forEach(id => newSet.add(id));
      return newSet;
    });
  }, []);

  // Handle conversation selection
  const handleConversationSelect = (conversationId: string) => {
    projectExpand(projectId);
    if (externalOnConversationSelect) {
      externalOnConversationSelect(conversationId, projectId);
    }
  };

  // Handle delete single conversation
  const handleDeleteSingleConversation = async (conversationId: string) => {
    await confirmConversationAction(
      {
        title: 'Delete conversation',
        message:
          'Are you sure you want to delete this conversation? This action cannot be undone.',
      },
      async () => {
        const res: any =
          await conversationActionsHook.deleteConversation(conversationId);
        if (res?.code === 200) {
          showToast(t('toast.conversationDeletedSuccessfully'), 'success');
        }
      }
    );
  };

  // Handle menu actions from ConversationListPanel (ProjectDetail - no project menu options)
  const handleMenuAction = useCallback(
    (action: string, conversationId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      const conversation = filteredConversations.find(
        c => c.conversationId === conversationId
      );
      if (!conversation) return;

      switch (action) {
        case 'select':
          toggleConversationSelect(conversationId, event);
          break;
        case 'star':
          // 🔥 archived 项目禁止 star conversation
          if (isArchived) return;
          conversationActionsHook.toggleStar(
            conversationId,
            conversation.starred || false
          );
          break;
        case 'rename':
          setRenameConversationId(conversationId);
          setRenameValue(conversation.title);
          setShowConversationRenameModal(true);
          break;
        case 'delete':
          handleDeleteSingleConversation(conversationId);
          break;
        case 'share':
          // 🔥 archived 项目禁止 share conversation
          if (isArchived) return;
          setShareModal({
            isOpen: true,
            conversationId: conversation.conversationId,
            conversationTitle: conversation.title,
          });
          break;
        // ProjectDetail doesn't support project menu operations (changeProject, removeFromProject, addToProject)
      }
    },
    [
      filteredConversations,
      conversationActionsHook.toggleStar,
      toggleConversationSelect,
      setShareModal,
      isArchived, // 🔥 添加 isArchived 依赖，确保 archived 检查使用最新值
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ]
  );

  // Handle bulk delete of conversations
  const handleBulkDeleteConversations = async () => {
    if (selectedConversationIds.size === 0) return;
    const count = selectedConversationIds.size;
    const ids = Array.from(selectedConversationIds);
    await confirmConversationAction(
      {
        title: 'Delete conversations',
        message: `Are you sure you want to delete ${count} conversation${count > 1 ? 's' : ''}? This action cannot be undone.`,
      },
      async () => {
        // 只做 API 调用，不等待 refresh（让对话框快速关闭）
        const res: any = await apiDelete('/api/ai/conversations/batch', {
          conversationIds: ids,
          _method: 'DELETE',
        });
        if (res?.code === 200) {
          showToast(
            t('toast.conversationsDeletedSuccessfully', { count }),
            'success'
          );
          setSelectedConversationIds(new Set());
          // 异步刷新，不阻塞对话框关闭
          refreshConversations();
          // Only clear the current project's cache, not the entire cache
          setSidebarProjectConversationsCache(prev => {
            const newCache = { ...prev };
            delete newCache[projectId];
            return newCache;
          });
          // 🔥 刷新侧边栏 Recent Chats 和 Starred 列表
          window.refreshSidebarRecent?.();
          window.refreshSidebarStarred?.();
          // 🔥 同步 ChatsPage 的本地 conversations 状态
          if (window.removeConversationFromPages) {
            ids.forEach(id => window.removeConversationFromPages?.(id));
          }
        }
      }
    );
  };

  // Remove selected conversations from project
  const handleRemoveFromProject = async () => {
    if (selectedConversationIds.size === 0) return;
    const count = selectedConversationIds.size;
    await confirmConversationAction(
      {
        title: t('chat.removeFromProject'),
        message: t('confirmDialog.removeFromProjectConfirm', { count }),
        confirmText: t('common.confirm'),
        type: 'warning',
      },
      async () => {
        try {
          await conversationActionsHook.moveToProject(
            Array.from(selectedConversationIds),
            null
          );
          setSelectedConversationIds(new Set());
          try {
            const { ensureNonCommonSelected } =
              await import('@/lib/agentUtils');
            ensureNonCommonSelected();
          } catch (e) {
            console.warn('Auto-switch agent after removeFromProject failed', e);
          }
        } catch (error) {
          console.error('Failed to remove conversations from project:', error);
        }
      }
    );
  };

  // Handle rename conversation
  const handleRenameConversation = async (newName?: string) => {
    const nameToUse = newName?.trim() || renameValue.trim();
    if (!renameConversationId || !nameToUse) return;
    setIsRenamingConversation(true);
    try {
      const result = await conversationActionsHook.renameConversation(
        renameConversationId,
        nameToUse
      );
      // 只有成功时才关闭对话框
      if (result?.code === 200) {
        setShowConversationRenameModal(false);
        setRenameConversationId(null);
        setRenameValue('');
      }
    } catch (error: any) {
      console.error(error?.message || 'Failed to rename conversation:', error);
    } finally {
      setIsRenamingConversation(false);
    }
  };

  // 处理文件夹点击
  const handleFolderClick = (folder: FileNode) => {
    // 使用 id 作为路径标识，更可靠
    setCurrentPath(prev => [...prev, folder]);
    // 切换目录时清除文件选中状态
    setSelectedFileIds(new Set());
  };

  // 处理面包屑导航
  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setCurrentPath([]);
    } else {
      setCurrentPath(prev => prev.slice(0, index + 1));
    }
    // 切换目录时清除文件选中状态
    setSelectedFileIds(new Set());
  };

  // 处理文件选择
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      try {
        const targetPath = '/' + currentPath.join('/');
        await onFileUpload(files, targetPath);
        showToast(
          t('toast.filesUploadedSuccessfully', { count: files.length }),
          'success'
        );
      } catch (error) {
        showToast(
          error instanceof Error
            ? error.message
            : t('toast.failedToUploadFiles'),
          'error'
        );
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 触发文件选择
  const triggerFileUpload = () => {
    fileUploadHook.uploadInputRef.current?.click();
  };

  // Initialize effect
  useEffect(() => {
    loadConversations();
    // 每次进入组件时强制刷新项目文件，确保与后端同步
    const refreshFiles = async () => {
      try {
        resetProjectFilesCache(projectId);
        await loadProjectFilesFromStore(projectId);
      } catch (e) {
        console.error('Failed to refresh project files on enter:', e);
      }
    };
    refreshFiles();

    // 防止整个页面的拖拽文件默认行为（在新标签页打开或下载）
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // 在dragover和drop事件中阻止默认行为
    window.addEventListener('dragover', preventDefaults, false);
    window.addEventListener('drop', preventDefaults, false);

    return () => {
      window.removeEventListener('dragover', preventDefaults, false);
      window.removeEventListener('drop', preventDefaults, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // 格式化对话日期
  const formatConversationDate = (date: string) => {
    const now = new Date();
    const targetDate = new Date(date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );
    const ONE_DAY = 1000 * 60 * 60 * 24;
    const diffDays = Math.round((today.getTime() - target.getTime()) / ONE_DAY);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays >= 2 && diffDays < 32) return `${diffDays} days ago`;
    return targetDate.toLocaleDateString();
  };

  // Open file preview
  const openFilePreview = (node: FileNode) => {
    const fileUrl = `${window.location.origin}/api/projects/${projectId}/files/content?filePath=${encodeURIComponent(node.path || node.name)}&Authorization=${localStorage.getItem('user-token')}&asAttachment=false`;
    setPreviewFile(node);
    setPreviewFileUrl(fileUrl);
  };

  // Close file preview
  const closeFilePreview = () => {
    setPreviewFile(null);
    setPreviewFileUrl(null);
  };

  // Handle add text content
  const handleAddTextContent = async () => {
    if (!addTextContentTitle.trim() || !addTextContentContent.trim()) return;

    setIsAddingTextContent(true);
    try {
      // Build the file path based on current folder, with .txt extension
      const currentFolderId = getCurrentFolderId();
      const fileName = `${addTextContentTitle.trim()}.txt`;
      const filePath = currentFolderId
        ? `${currentFolderId}/${fileName}`
        : fileName;

      const res = await apiPost<any>(
        `/api/projects/${projectId}/files/content`,
        {
          filePath,
          content: addTextContentContent,
        }
      );

      if (res?.code === 200) {
        showToast(t('toast.contentAddedSuccessfully'), 'success');
        setIsAddTextContentModalOpen(false);
        setAddTextContentTitle('');
        setAddTextContentContent('');
        // Refresh files
        resetProjectFilesCache(projectId);
        await loadProjectFilesFromStore(projectId);
      }
    } catch (error) {
      console.error('Failed to add text content:', error);
      showToast(t('toast.failedToAddTextContent'), 'error');
    } finally {
      setIsAddingTextContent(false);
    }
  };

  // Open add text content modal
  const openAddTextContentModal = () => {
    setAddTextContentTitle('');
    setAddTextContentContent('');
    setIsAddTextContentModalOpen(true);
  };

  return {
    // Refs
    fileInputRef,
    filesContainerRef,
    fileItemRefs,

    // Search
    searchQuery,

    // Instructions
    instructions,
    setInstructions,
    isInstructionsModalOpen,
    setIsInstructionsModalOpen,
    tempInstructions,
    setTempInstructions,
    originalInstructions,
    setOriginalInstructions,
    isSavingInstructions,
    handleSaveInstructions,

    // Members
    showMembers,
    setShowMembers,

    // Star
    isStarred,
    isStarring,
    handleToggleStar,

    // Archive
    isArchived,
    isArchiving,
    handleToggleArchive,

    // Edit project modal
    isEditProjectModalOpen,
    setIsEditProjectModalOpen,

    // Folder hover
    hoveredFolderId,
    setHoveredFolderId,

    // Conversation selection
    selectedConversationIds,
    setSelectedConversationIds,
    toggleConversationSelect,
    handleBatchSelect,
    handleBulkDeleteConversations,
    handleRemoveFromProject,

    // Conversation confirm dialog
    conversationDialogProps,

    // Conversation rename
    renameConversationId,
    renameValue,
    setRenameValue,
    showConversationRenameModal,
    setShowConversationRenameModal,
    isRenamingConversation,
    handleRenameConversation,

    // Share modal
    shareModal,
    setShareModal,

    // File selection
    selectedFileIds,
    setSelectedFileIds,
    hoveredFileId,
    setHoveredFileId,
    toggleFileSelect,
    clearFileSelection,
    handleSelectAllFiles,
    allFilesSelected,
    totalSelectableFiles,

    // Files browser
    fileTree,
    currentPath,
    setCurrentPath,
    isLoadingFiles,
    fileSearchQuery,
    setFileSearchQuery,
    currentNodes,
    currentFolderDepth,
    isMaxDepth,

    // File preview
    previewFile,
    previewFileUrl,
    openFilePreview,
    closeFilePreview,

    // Add text content
    isAddTextContentModalOpen,
    setIsAddTextContentModalOpen,
    addTextContentTitle,
    setAddTextContentTitle,
    addTextContentContent,
    setAddTextContentContent,
    isAddingTextContent,
    handleAddTextContent,
    openAddTextContentModal,

    // Conversations
    conversations,
    isLoading,
    filteredConversations,
    visibleConversations,
    hasMoreConversations,
    hasLoadingConversation,
    handleShowMoreConversations,
    handleConversationSelect,
    handleMenuAction,
    formatConversationDate,

    // New chat
    handleNewChat,

    // Folder/File operations
    handleFolderClick,
    handleBreadcrumbClick,
    handleFileSelect,
    triggerFileUpload,

    // File upload hook
    fileUploadHook,

    // Folder operations hook
    folderOperationsHook,

    // Files box selection
    filesBoxSelection,

    // Update project callback
    onUpdateProject,
  };
}
